import React, { useState } from "react";

import WarningIcon from "@mui/icons-material/Warning";
import {
  Box,
  Button,
  CircularProgress,
  Portal,
  Typography,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";

import Dialog from "@/components/Dialog";
import Padding from "@/components/layout/Padding";
import useDialogState from "@/hooks/useDialogState";
import DeleteIconButton from "@/pages/Clients/IconButtons/DeleteIconButton";
import { colors } from "@/theme/overrides/colors";

const useStyles = makeStyles()({
  deleteDialogPaper: {
    minWidth: 400,
    maxWidth: 400,
  },
  deleteDialogContent: {
    paddingTop: 0,
  },
  deleteDialogIcon: {
    maxHeight: 60,
    fontSize: 60,
    color: colors.secondary,
  },
  deleteDialogTitle: {
    fontWeight: 700,
    fontSize: 28,
    lineHeight: 1,
    color: colors.dark,
    textAlign: "center",
  },
  deleteDialogMessage: {
    fontWeight: 600,
    fontSize: 16,
    lineHeight: "22px",
    color: colors.gray,
    textAlign: "center",
  },
  deleteDialogButton: {
    minWidth: 125,
  },
  itemIcon: {
    minWidth: 30,
  },
});

type DeleteWeightProps = {
  title: string;
  message: string;
  onDelete: (id?: string) => void;
};

const DeleteWeight: React.FC<DeleteWeightProps> = ({
  onDelete,
  title,
  message,
}) => {
  const { classes } = useStyles();
  const deleteDialog = useDialogState();
  const [isDeleting, setIsDeleting] = useState(false);

  const openDeleteDialog = () => {
    deleteDialog.show();
  };

  const closeDeleteDialog = () => {
    setIsDeleting(false);
    deleteDialog.hide();
  };

  const handleDeleteConfirm = () => {
    setIsDeleting(true);
    onDelete();
    closeDeleteDialog();
  };

  return (
    <>
      <DeleteIconButton onClick={openDeleteDialog} />
      <Portal>
        <Dialog
          title={title}
          open={deleteDialog.shown}
          onClose={deleteDialog.hide}
          classes={{ paper: classes.deleteDialogPaper }}
          contentClassName={classes.deleteDialogContent}
          content={
            <Box display="flex" margin="8px" alignItems="center">
              <Box display="flex" margin={9} alignItems="center">
                <div className={classes.deleteDialogIcon}>
                  <WarningIcon fontSize="inherit" color="inherit" />
                </div>
                <Typography className={classes.deleteDialogTitle}>
                  Are you sure?
                </Typography>
              </Box>
              <Padding horizontal={4}>
                <Typography className={classes.deleteDialogMessage}>
                  {message}
                </Typography>
              </Padding>
            </Box>
          }
          actions={
            <Button
              size="large"
              className={classes.deleteDialogButton}
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
            >
              {isDeleting ? <CircularProgress size="1.6rem" /> : <>Confirm</>}
            </Button>
          }
        />
      </Portal>
    </>
  );
};

export default DeleteWeight;
